import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RadioInput } from 'components/form';
import { toTitleCase } from 'utils/titleCase';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type QualityOption = { title: string; description?: string };

type WorkCultureTraitFormData = {
  workCultureTraits: {
    title: string;
    description: string;
    checked: boolean;
  }[];
  candidateQualities: string[];
  qualityOptions: QualityOption[];
};

const workCultureTraitSchema = yup.object().shape({
  workCultureTraits: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Trait name is required'),
      description: yup.string().required('Description is required'),
      checked: yup.boolean().default(false),
    })
  )
    .required('At least one work culture trait is required')
    .min(1, 'At least one work culture trait is required')
    .test(
      'max-checked',
      'You can select a maximum of 3 traits',
      (traits) => traits.filter(trait => trait.checked).length <= 3
    ),
  candidateQualities: yup
    .array()
    .of(yup.string().required('Candidate qualities option cannot be undefined'))
    .min(1, 'Select at least one candidate quality')
    .required('Candidate qualities is required'),
  qualityOptions: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required(),
        description: yup.string(),
      })
    )
    .default([]),
});

interface WorkCultureTraitsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: WorkCultureTraitFormData;
  onSave: (data: WorkCultureTraitFormData) => void;
  isSubmitting?: boolean;
}

export const WorkCultureTraitsEditModal: React.FC<WorkCultureTraitsEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting
}) => {
  const defaultValues: WorkCultureTraitFormData = {
    workCultureTraits: currentData?.workCultureTraits || [],
    candidateQualities: currentData?.candidateQualities || [],
    qualityOptions: currentData?.qualityOptions || [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch
  } = useForm<WorkCultureTraitFormData>({
    defaultValues,
    resolver: yupResolver(workCultureTraitSchema)
  });

  const { fields } = useFieldArray({
    control,
    name: 'workCultureTraits'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData]);

  const onSubmit = (data: WorkCultureTraitFormData) => {
    onSave(data);
  };

  const checkedCount = watch('workCultureTraits').filter(trait => trait.checked).length;

  const candidateQualitiesOptions = (currentData?.qualityOptions || []).map((option) => ({
    value: option.title,
    label: toTitleCase(option.title),
    description: option.description || '',
  }));

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Work Culture Traits"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto mt-[10px] mb-[30px]">
        <p className="mb-[20px] pb-2.5 border-b border-[#D0D5DD]">
          You can select up to 3 Work Culture Traits that best resonate with you
        </p>
        <h3 className="text-lg font-semibold text-[#1D2939] mb-1">Work culture traits</h3>
        <p className="text-[#475467] mb-[15px] text-sm">Maximum of three (3) selections</p>

        {errors.workCultureTraits && typeof errors.workCultureTraits === 'object' && 'message' in errors.workCultureTraits && (
          <small className="text-xs text-[#e74444] block mb-3">{errors.workCultureTraits.message as string}</small>
        )}

        <div className="grid grid-cols-2 gap-x-[30px] gap-y-[10px]">
          {fields.map((trait, index) => (
            <div key={trait.id} className="items-start gap-3">
              <RadioInput
                register={register}
                name={`workCultureTraits.${index}.checked`}
                validatorMessage={errors.workCultureTraits?.[index]?.checked?.message}
                options={[{
                  label: toTitleCase(trait.title),
                  description: trait.description,
                  value: 'true',
                }]}
                type="checkbox"
                parentClassName="grid gap-[5%] !mb-0"
                labelClassName='!mb-1'
                disabled={checkedCount >= 3 && !watch(`workCultureTraits.${index}.checked`)}
              />
            </div>
          ))}
        </div>

        <p className="font-medium text-base lg:text-lg mt-[30px] mb-[20px] border-t border-[#D0D5DD] pt-[20px]">
          Choose three (3) Relevant Candidate Qualities
        </p>

        <RadioInput
          register={register}
          name="candidateQualities"
          validatorMessage={errors.candidateQualities?.message}
          options={candidateQualitiesOptions}
          type="checkbox"
          parentClassName="grid gap-[5%] !mb-0"
        />
      </div>
    </BaseEditModal>
  );
};
